import React, { useState, useEffect, useMemo } from 'react';
import { ipApi } from '../../services/api';
import { Q, DEFAULT_QUEUE_TAGS } from '../../config/queueStatus';
import { useHospitalDirectory, formatDoctorOption } from '../../hooks/useHospitalDirectory';
import { servicePresetsFromSettings, serviceNamesFromSettings } from '../../config/emrSettings';
import { printOpToken, printOpReceipt } from '../../utils/hospitalPdf';

const STEPS = ['Patient', 'Demographics', 'History', 'Visit', 'Confirm'];

const emptyForm = {
  patientName: '', patientId: '', patientPhone: '', age: '', gender: 'M',
  bloodGroup: '', address: '', allergies: '', medicalHistory: '',
  visitType: 'Walk-in', doctor: '', fee: 500, paymentMode: 'Cash',
  service: 'Consultation', reason: '', tags: [], billBeforeConsult: false,
};

export default function WalkInWizard({ hospital, user, patients, onDone, onCancel, emrSettings = {} }) {
  const { data: dir } = useHospitalDirectory();
  const doctors = useMemo(() => dir?.doctors || [], [dir?.doctors]);
  const servicePresets = useMemo(() => servicePresetsFromSettings(emrSettings), [emrSettings]);
  const services = useMemo(() => serviceNamesFromSettings(emrSettings, ['Consultation', 'Health Check-up']), [emrSettings]);
  const defaultFee = servicePresets[0]?.amount || dir?.hospital?.pricing?.opConsultFee || 500;
  const defaultService = servicePresets[0]?.label || 'Consultation';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm, fee: defaultFee, service: defaultService });
  const [patientPick, setPatientPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uhidPreview, setUhidPreview] = useState('');

  useEffect(() => {
    if (doctors.length && !form.doctor) {
      setForm((f) => ({ ...f, doctor: doctors[0].name, fee: defaultFee }));
    }
  }, [doctors, defaultFee, form.doctor]);

  const pickPatient = (patientId) => {
    const p = (patients || []).find((x) => x.patientId === patientId);
    if (!p) return;
    setForm((f) => ({
      ...f,
      patientName: p.name,
      patientId: p.patientId,
      patientPhone: p.phone || '',
      age: p.age || '',
      gender: p.gender || 'M',
      bloodGroup: p.bloodGroup || '',
      address: p.address || '',
      allergies: p.allergies || '',
      medicalHistory: (p.chronicConditions || []).join(', '),
    }));
    setUhidPreview(p.patientId);
    setPatientPick('');
  };

  const generateUhid = () => {
    // Display-only hint: the real UHID is allocated atomically by the server on save.
    const prefix = hospital?.uhidPrefix || 'HCEN';
    setUhidPreview(`${prefix}-(auto)`);
  };

  const toggleTag = (tag) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag],
    }));
  };

  const next = () => {
    if (step === 0) {
      if (!form.patientName.trim()) { setError('Patient name is required.'); return; }
      if (!form.patientId && !uhidPreview) generateUhid();
    }
    setError('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => { setError(''); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async (checkIn = true) => {
    setBusy(true);
    setError('');
    try {
      let patientId = form.patientId || uhidPreview;
      const existing = (patients || []).find((p) => p.patientId === patientId);
      if (!existing && form.patientName.trim()) {
        // New patient: let the server allocate the UHID atomically (previewed
        // ids can collide when two receptionists register at the same time).
        const pr = await ipApi.createPatient({
          autoUhid: true,
          name: form.patientName.trim(),
          phone: form.patientPhone,
          age: form.age,
          gender: form.gender,
          address: form.address,
          bloodGroup: form.bloodGroup,
          allergies: form.allergies,
          chronicConditions: form.medicalHistory ? form.medicalHistory.split(',').map((s) => s.trim()).filter(Boolean) : [],
          hospitalId: user.hospitalId,
        });
        patientId = pr?.patient?.patientId || patientId;
      }

      const payNow = form.billBeforeConsult && ['Cash', 'UPI / GPay', 'Card'].includes(form.paymentMode);
      const res = await ipApi.addToQueue({
        ...form,
        patientId,
        hospitalId: user.hospitalId,
        checkIn,
        status: checkIn ? (payNow || form.billBeforeConsult ? Q.AWAITING_PAYMENT : Q.CHECKED_IN) : Q.BOOKED,
        visitDate: new Date().toISOString().slice(0, 10),
      });

      if (form.billBeforeConsult && res?.queue) {
        const receiptRes = await ipApi.createOpReceipt({
          patientName: form.patientName,
          patientId,
          amount: form.fee,
          queueId: res.queue.queueId,
          paymentMode: form.paymentMode,
          service: form.service,
          hospitalId: user.hospitalId,
        });
        if (receiptRes?.receipt) printOpReceipt(receiptRes.receipt, hospital, user);
      }

      if (res?.queue) printOpToken(res.queue, hospital, user);
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb q-wizard">
      <div className="q-wizard-head">
        <h3 style={{ margin: 0, color: '#0d6e6a' }}>+ New walk-in</h3>
        <div className="q-wizard-steps">
          {STEPS.map((label, i) => (
            <span key={label} className={`q-step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}>
              {i + 1}. {label}
            </span>
          ))}
        </div>
      </div>

      {error && <div className="error mb">{error}</div>}

      {step === 0 && (
        <div className="form-grid">
          <div className="field full">
            <label>Registered patient (optional)</label>
            <select className="input" value={patientPick} onChange={(e) => { setPatientPick(e.target.value); pickPatient(e.target.value); }}>
              <option value="">— Search UHID or register new below —</option>
              {(patients || []).map((p) => (
                <option key={p.patientId} value={p.patientId}>{p.name} · {p.patientId}</option>
              ))}
            </select>
          </div>
          <div className="field"><label>Patient name *</label><input className="input" required value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} /></div>
          <div className="field"><label>Mobile</label><input className="input" value={form.patientPhone} onChange={(e) => setForm({ ...form, patientPhone: e.target.value })} placeholder="10-digit" /></div>
          <div className="field full">
            <label>UHID</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="input" value={form.patientId || uhidPreview} readOnly placeholder="Auto-generated on next" />
              <button type="button" className="btn btn-secondary btn-sm" onClick={generateUhid}>Generate UHID</button>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="form-grid">
          <div className="field"><label>Age</label><input className="input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></div>
          <div className="field">
            <label>Gender</label>
            <select className="input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
            </select>
          </div>
          <div className="field">
            <label>Blood group</label>
            <select className="input" value={form.bloodGroup} onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })}>
              <option value="">—</option>
              {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="field full"><label>Address</label><input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        </div>
      )}

      {step === 2 && (
        <div className="form-grid">
          <div className="field full"><label>Allergies</label><input className="input" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="e.g. Penicillin" /></div>
          <div className="field full"><label>Medical history / chronic conditions</label><textarea className="input" rows={3} value={form.medicalHistory} onChange={(e) => setForm({ ...form, medicalHistory: e.target.value })} placeholder="Diabetes, HTN, asthma…" /></div>
        </div>
      )}

      {step === 3 && (
        <div className="form-grid">
          <div className="field">
            <label>Doctor *</label>
            <select className="input" value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })}>
              {doctors.map((d) => <option key={d.staffId} value={d.name}>{formatDoctorOption(d)}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Service</label>
            <select className="input" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
              {services.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Fee ₹</label><input className="input" type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: Number(e.target.value) })} /></div>
          <div className="field">
            <label>Payment mode</label>
            <select className="input" value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
              {['Cash', 'UPI / GPay', 'Card', 'Insurance / TPA', 'Free / Waived'].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="field full"><label>Chief complaint</label><input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          <div className="field full">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.billBeforeConsult} onChange={(e) => setForm({ ...form, billBeforeConsult: e.target.checked })} />
              Bill before consultation
            </label>
          </div>
          <div className="field full">
            <label>Tags</label>
            <div className="q-tag-row">
              {DEFAULT_QUEUE_TAGS.map((t) => (
                <button key={t} type="button" className={`q-tag${form.tags.includes(t) ? ' on' : ''}`} onClick={() => toggleTag(t)}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="q-confirm card" style={{ background: '#f8fafc' }}>
          <p><strong>{form.patientName}</strong> · {form.patientId || uhidPreview} · {form.age}y {form.gender}</p>
          <p>{form.doctor} · {form.service} · ₹{form.fee} ({form.paymentMode})</p>
          {form.reason && <p>Complaint: {form.reason}</p>}
          {form.tags.length > 0 && <p>Tags: {form.tags.join(', ')}</p>}
        </div>
      )}

      <div className="form-actions" style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        {step > 0 && <button type="button" className="btn btn-secondary" onClick={back}>Back</button>}
        {step < STEPS.length - 1 ? (
          <button type="button" className="btn btn-primary" onClick={next}>Next</button>
        ) : (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => submit(true)}>
            {busy ? 'Checking in…' : '✓ Check-in & print token'}
          </button>
        )}
      </div>
    </div>
  );
}
